import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ErroAPI, api, data, dataHora, desde } from "../api.ts";
import { Cartao, Selo, SeloSituacao, SeloStatus, Vazio } from "../componentes/basicos.tsx";
import { Serie } from "../componentes/serie.tsx";
import { podeEscrever, useSessao } from "../sessao.tsx";
import { useDados } from "../usarDados.ts";

type Pico = { picoJanela: number; picoHistorico: number; diaDoPico: string | null };
type Resposta = {
  empresa: {
    id: number; razao_social: string; fantasia: string; cnpj: string;
    email: string; telefone: string; cidade: string; uf: string;
    ativo: boolean; criado_em: string; situacao: string;
    maquinas_ativas: number; vagas: number; ultimo_sinal: string | null;
  };
  licencas: {
    id: number; apelido: string; status: string; validade: string | null;
    max_instancias: number; max_maquinas: number; maquinas_ativas: number;
    modo_offline: string; ultimo_contato: string | null; pico: Pico | null;
  }[];
  auditoria: { id: number; ocorrido_em: string; ator: string; acao: string; ip: string }[];
  sinal: { dia: string; valor: number }[];
};

export function Empresa() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [criando, setCriando] = useState("");
  const { dados: r, erro, carregando } = useDados<Resposta>(`/empresas/${id}`);

  if (carregando) return <div className="carregando">carregando...</div>;
  if (erro || !r) return <div className="alerta alerta--erro">{erro}</div>;
  const e = r.empresa;

  return (
    <>
      <div className="entre" style={{ marginBottom: "var(--e-lg)" }}>
        <div>
          <h1 className="mb-0">{e.razao_social}</h1>
          {/* E-mail e telefone no cabecalho, nao enterrados no formulario de
              edicao: e justamente o dado que se precisa quando o alerta manda
              ligar para o cliente. */}
          <div className="linha pequeno suave" style={{ marginTop: 4 }}>
            <span className="mono">{e.cnpj}</span>
            {e.cidade && <span>{e.cidade}/{e.uf}</span>}
            {e.email && <a href={`mailto:${e.email}`}>{e.email}</a>}
            {e.telefone && <span>{e.telefone}</span>}
            <span>cliente desde {data(e.criado_em)}</span>
          </div>
        </div>
        <div className="linha">
          <SeloSituacao situacao={e.situacao} />
          <Link className="botao botao--secundario" to="/empresas">Voltar</Link>
        </div>
      </div>

      <div className="tiles">
        <div className="tile">
          <div className="tile__valor">{r.licencas.filter((l) => l.status === "ativa").length}</div>
          <div className="tile__rotulo">Licencas ativas</div>
          <div className="tile__sub">de {r.licencas.length} no total</div>
        </div>
        <div className="tile">
          <div className="tile__valor">{e.maquinas_ativas}</div>
          <div className="tile__rotulo">Maquinas</div>
          <div className="tile__sub">de {e.vagas} vagas contratadas</div>
        </div>
        <div className="tile">
          <div className="tile__valor">
            {r.licencas.reduce((s, l) => s + (l.pico?.picoJanela ?? 0), 0)}
          </div>
          <div className="tile__rotulo">Instancias no pico</div>
          <div className="tile__sub">ultimos 30 dias</div>
        </div>
        <div className="tile">
          <div className="tile__valor" style={{ fontSize: "var(--t-h2)" }}>{desde(e.ultimo_sinal)}</div>
          <div className="tile__rotulo">Ultimo sinal</div>
          <div className="tile__sub">de qualquer maquina desta empresa</div>
        </div>
      </div>

      <Cartao titulo="Licencas" acoes={podeEscrever(usuario) ? (
        <button className="botao botao--pequeno" type="button" disabled={criando === "indo"}
                onClick={() => {
                  setCriando("indo");
                  void (async () => {
                    try {
                      // A licenca nasce com os padroes do banco e a tela dela
                      // abre em seguida: e la que estao os limites E o botao de
                      // gerar codigo. Um formulario aqui so adiaria a mesma
                      // tela, e a pressa de quem esta no telefone e chegar ao
                      // codigo.
                      const n = await api.post<{ id: number }>(`/empresas/${id}/licencas`);
                      navegar(`/licencas/${n.id}`);
                    } catch (e) {
                      setCriando(e instanceof ErroAPI ? e.message : "Nao consegui criar.");
                    }
                  })();
                }}>Nova licenca</button>
      ) : undefined}>
        {criando && criando !== "indo" && <div className="alerta alerta--erro">{criando}</div>}
        {r.licencas.length === 0 ? (
          <Vazio titulo="Sem licencas">
            Crie a primeira aqui em cima. E dentro dela que sai o codigo de ativacao
            que o tecnico digita no Automatiza.
          </Vazio>
        ) : (
          <div className="tabela-rolagem">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Licenca</th><th>Status</th><th>Validade</th><th>Maquinas</th>
                  <th>Instancias</th><th>Sem contato</th><th>Ultimo contato</th>
                </tr>
              </thead>
              <tbody>
                {r.licencas.map((l) => (
                  <tr key={l.id}>
                    <td><Link to={`/licencas/${l.id}`}>{l.apelido || `Licenca ${l.id}`}</Link></td>
                    <td><SeloStatus status={l.status} /></td>
                    <td>{l.validade ? data(l.validade) : <span className="apagado">perpetua</span>}</td>
                    <td className="tabela__num">{l.maquinas_ativas} / {l.max_maquinas}</td>
                    {/* Pico REAL contra o contratado. Mostrar so max_instancias
                        seria numero de contrato se passando por numero de uso. */}
                    <td className="tabela__num">
                      {l.pico?.picoJanela ?? 0} / {l.max_instancias}
                    </td>
                    <td>
                      {l.modo_offline === "bloquear"
                        ? <Selo tom="critico">bloqueia</Selo>
                        : <span className="apagado pequeno">so avisa</span>}
                    </td>
                    <td className="suave pequeno">{desde(l.ultimo_contato)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>

      <Cartao titulo="Sinal da empresa" pergunta="maquinas distintas que reportaram por dia">
        <Serie pontos={r.sinal} altura={100} rotuloValor="Maquinas" />
      </Cartao>

      <Cartao titulo="Historico desta empresa">
        {r.auditoria.length === 0 ? (
          <Vazio titulo="Sem registros" />
        ) : (
          <table className="tabela">
            <thead><tr><th>Quando</th><th>Quem</th><th>Acao</th><th>IP</th></tr></thead>
            <tbody>
              {r.auditoria.map((a) => (
                <tr key={a.id}>
                  <td className="suave pequeno">{dataHora(a.ocorrido_em)}</td>
                  <td>{a.ator}</td>
                  <td className="tabela__mono">{a.acao}</td>
                  <td className="tabela__mono">{a.ip || "--"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Cartao>
    </>
  );
}
